import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  Menu,
  X,
  Home,
  Grid,
  Image,
  Briefcase,
  Users,
  Settings,
  Edit3,
  User,
} from "lucide-react";
import { signOut } from "firebase/auth";
import { auth } from "@/firebase";

const menuItems = [
  { name: "Dashboard", path: "/admin-dashboard", icon: <Home size={18} /> },
  {
    name: "Projects",
    path: "/admin-dashboard/projects",
    icon: <Grid size={18} />,
  },
  {
    name: "Gallery",
    path: "/admin-dashboard/gallery",
    icon: <Image size={18} />,
  },
  {
    name: "Careers",
    path: "/admin-dashboard/careers",
    icon: <Briefcase size={18} />,
  },
  {
    name: "Job Applications",
    path: "/admin-dashboard/applicants",
    icon: <Users size={18} />,
  },
  {
    name: "Contact Settings",
    path: "/admin-dashboard/contact-settings",
    icon: <Settings size={18} />,
  },
  { name: "Blog", path: "/admin-dashboard/blog", icon: <Edit3 size={18} /> },
  {
    name: "Profile",
    path: "/admin-dashboard/profile",
    icon: <User size={18} />,
  },
];

export default function AdminLayout() {
  const [desktopOpen, setDesktopOpen] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  const handleLogout = () => {
    setShowLogoutConfirm(true); // open confirmation modal
  };

  const confirmLogout = async () => {
    await signOut(auth);
    navigate("/login");
  };

  const cancelLogout = () => {
    setShowLogoutConfirm(false);
  };

  return (
    <div className="flex min-h-screen bg-[var(--bg-1)] relative font-sans text-brand-dark">
      {/* Desktop Sidebar */}
      <aside
        className={`hidden md:flex bg-brand-dark text-white flex-col transition-all duration-300 ${
          desktopOpen ? "w-64" : "w-20"
        }`}
      >
        <div className="flex items-center justify-between p-6 border-b border-gray-700">
          <h2
            className={`text-2xl font-bold text-brand-gold truncate transition-all duration-300 ${
              desktopOpen ? "" : "opacity-0 w-0"
            }`}
          >
            DVV Admin
          </h2>
          <button
            onClick={() => setDesktopOpen(!desktopOpen)}
            className="hover:text-brand-gold"
          >
            {desktopOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>

        <nav className="flex-1 flex flex-col mt-4">
          {menuItems.map((item) => (
            <NavLink
              key={item.name}
              to={item.path}
              end={item.path === "/admin-dashboard"}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 transition-colors rounded-lg mx-2 my-1 hover:bg-[var(--brand-gold)] hover:text-white ${
                  isActive
                    ? "bg-[var(--brand-gold)] text-white font-semibold"
                    : "text-white"
                }`
              }
            >
              {item.icon}
              {desktopOpen && <span>{item.name}</span>}
            </NavLink>
          ))}
        </nav>

        <div className="p-4 border-t border-gray-700 mt-auto">
          <button
            onClick={handleLogout}
            className="w-full py-2 rounded-md text-red-400 hover:bg-red-500 hover:text-white transition font-semibold flex items-center justify-center gap-2"
          >
            <X size={18} />
            {desktopOpen && "Logout"}
          </button>
        </div>
      </aside>

      <aside className="flex md:hidden flex-col bg-brand-dark text-white w-16 h-screen">

  {/* Hamburger button at top */}
  <button
    onClick={() => setMobileOpen(true)}
    className="flex items-center justify-center h-14 hover:text-brand-gold border-b border-gray-700"
  >
    <Menu size={20} />
  </button>

  {/* Menu items */}
  <div className="flex-1 flex flex-col">
    {menuItems.map((item) => (
      <NavLink
        key={item.name}
        to={item.path}
        end={item.path === "/admin-dashboard"}
        className={({ isActive }) =>
          `flex items-center justify-center h-14 transition-colors hover:bg-[var(--brand-gold)] hover:text-white ${
            isActive ? "bg-[var(--brand-gold)] text-white" : "text-white"
          }`
        }
      >
        {item.icon}
      </NavLink>
    ))}
  </div>

  {/* Logout at bottom */}
  <button
    onClick={handleLogout}
    className="flex items-center justify-center h-14 text-red-400 hover:bg-red-500 hover:text-white transition"
    title="Logout"
  >
    <X size={20} />
  </button>

</aside>


      {/* Mobile overlay menu */}
      <div
        className={`fixed inset-0 z-50 md:hidden transition-all duration-300 ${
          mobileOpen ? "visible opacity-100" : "invisible opacity-0"
        }`}
      >
        <div
          className="absolute inset-0 bg-black bg-opacity-50"
          onClick={() => setMobileOpen(false)}
        ></div>

        <aside
          className={`absolute inset-y-0 left-0 w-64 bg-brand-dark text-white flex flex-col transform transition-transform duration-300 ${
            mobileOpen ? "translate-x-0" : "-translate-x-full"
          }`}
        >
          <div className="flex items-center justify-between p-6 border-b border-gray-700">
            <h2 className="text-2xl font-bold text-brand-gold">DVV Admin</h2>
            <button onClick={() => setMobileOpen(false)}>
              <X size={20} />
            </button>
          </div>

          <nav className="flex-1 flex flex-col mt-4">
            {menuItems.map((item) => (
              <NavLink
                key={item.name}
                to={item.path}
                end={item.path === "/admin-dashboard"}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-6 py-3 transition-colors rounded-lg mx-2 my-1 hover:bg-[var(--brand-gold)] hover:text-white ${
                    isActive
                      ? "bg-[var(--brand-gold)] text-white font-semibold"
                      : "text-white"
                  }`
                }
                onClick={() => setMobileOpen(false)}
              >
                {item.icon}
                <span>{item.name}</span>
              </NavLink>
            ))}
          </nav>

          <div className="p-4 border-t border-gray-700 mt-auto">
            <button
              onClick={handleLogout}
              className="w-full py-2 rounded-md text-red-400 hover:bg-red-500 hover:text-white transition font-semibold flex items-center justify-center gap-2"
            >
              <X size={18} />
              Logout
            </button>
          </div>
        </aside>
      </div>

      {/* Main content */}
      <main className="flex-1 p-6 md:p-8 overflow-auto">
        <Outlet />
      </main>
      {/* Logout Confirmation Modal */}
     {showLogoutConfirm && (
  <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50">
    <div className="bg-white rounded-2xl shadow-2xl p-6 w-[90%] max-w-sm">
      <h3 className="text-xl font-bold text-gray-800 mb-2">
        Confirm Logout
      </h3>
      <p className="text-gray-600 mb-6">
        Are you sure you want to logout?
      </p>

      <div className="flex justify-end gap-3">
        <button
          onClick={cancelLogout}
          className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 transition"
        >
          Cancel
        </button>
        <button
          onClick={confirmLogout}
          className="px-4 py-2 rounded-lg bg-red-500 text-white hover:bg-red-600 transition font-semibold"
        >
          Logout
        </button>
      </div>
    </div>
  </div>
)}

    </div>
  );
}
